import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box, Link, Grid, IconButton, InputAdornment } from '@mui/material';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../axios/endPoints';
import Loader from '../components/common/Loader';
import { useEffect, useState } from 'react';
import { EndPoints } from '../axios/endPoints';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../axios/axiosHandler';
import Logo from "../components/layout/utils/logo.svg";
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import LockTwoToneIcon from '@mui/icons-material/LockTwoTone';
import '../styles/Login.css';

const schema = (t: Function) =>
  yup.object().shape({
    email: yup.string().email(),
    password: yup.string().required(t('translations:RESET_PASSWORD_REQUIRED')),
    passwordConfirmation: yup
      .string()
      .required()
      .oneOf([yup.ref('password'), ''], t('translations:RESET_PASSWORD_MATCH_REQUIRED')),
  });


export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      email: searchParams.get('email') ?? '',
      password: '',
      passwordConfirmation: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetPassword = (data: any) => {
    if (error) return;
    setLoading(true);

    axiosInstance
      .post(EndPoints.RESET_PASSWORD, {
        email: data.email,
        password: data.password,
        token: searchParams.get('token'),
      })
      .then((payload : any) => {
        setLoading(false);

        setSuccess(payload.data.success);
        setError(payload.data.message);
      });
  };

  const passwordRecovered = () => {
    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography component="p">
          {t('translations:RESET_PASSWORD_SUCCESS_TITLE')}
        </Typography>
        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Link href={AppRoutes.LOG_IN} variant="body2">
            {t('translations:RESET_PASSWORD_LOGIN_BUTTON')}
          </Link>
        </Grid>
      </Box>
    );
  };

  const resetPasswordForm = () => {
    return (
      <Box
        component="form"
        onSubmit={handleSubmit(resetPassword)}
        sx={{ mt: 1, width: '100%' }}
      >
        <Controller
          name="email"
          control={control}
          render={({ field, formState }) => (
            <TextField
              {...field}
              disabled
              margin="normal"
              fullWidth
              placeholder={t('translations:RESET_PASSWORD_EMAIL_ADDRESS')}
              variant="outlined"
              type="email"
              error={!!formState.errors.email}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <EmailTwoToneIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field, formState }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              variant="outlined"
              type="password"
              placeholder={t('translations:RESET_PASSWORD_TEXTFIELD')}
              error={!!formState.errors?.password}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LockTwoToneIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          render={({ field, formState }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              variant="outlined"
              type="password"
              label={t('translations:RESET_PASSWORD_CONFIRM')}
              error={!!formState.errors?.passwordConfirmation}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LockTwoToneIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
          name="passwordConfirmation"
          control={control}
        />
        <Collapse in={error != null}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity="error"
          >
            {t(`translations:${error}`)}
          </Alert>
        </Collapse>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          className="button-color"
          sx={{ mt: 3, mb: 2 }}
        >
          {t('translations:RESET_PASSWORD_RECOVER_BUTTON')}
        </Button>
      </Box>
    );
  };

  useEffect(() => {
    document.title = t('translations:RESET_PASSWORD_PAGE_TITLE');
  }, [])

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          {t('translations:RESET_PASSWORD_TITLE')}
        </Typography>
        <Loader open={loading} />
        {!success ? resetPasswordForm() : passwordRecovered()}
      </Box>
    </Container>
  );
}
